import React, { createContext, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { parseSplitBlock } from '@kuutio/shared/splitBlock.js';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * The opinion agent's numbers as split bars: one fenced `kuutio-split` block
 * (JSON; packages/shared/splitBlock.js) drawn as one row per statement, the
 * groups' agree shares side by side, the all-participants share as a tick, the
 * row's ref as a small label linking to the source list. Renders in the drawer
 * message, the folded thread turn, the report panel and the artifact page; a
 * block that fails to parse falls back to a plain code block, never to a blank.
 *
 * SplitProvider gives the sources (ref → num, for the links) and the tone.
 */
const SplitContext = createContext({ sources: [], tone: 'dark' });
export function SplitProvider({ sources = [], tone = 'dark', children }) {
  const value = useMemo(() => ({ sources, tone }), [sources, tone]);
  return <SplitContext.Provider value={value}>{children}</SplitContext.Provider>;
}

// One hue per group, up to eight before they repeat; the same hues lifted for the dark drawer
const TONES = {
  light: { text: 'var(--color-grey-1, #292929)', muted: 'var(--color-grey-2, #636363)', track: 'var(--color-grey-5, #f2f2f2)', tick: '#292929',
    border: 'var(--color-grey-4, #d9d9d9)', bg: '#fff', ref: 'var(--color-primary, #3b78b0)', refBg: 'var(--color-blueberry-light, #dce8f3)',
    fills: ['hsl(209 55% 47%)', 'hsl(153 100% 26%)', 'hsl(31 100% 45%)', 'hsl(344 86% 49%)',
      'hsl(268 50% 52%)', 'hsl(186 80% 32%)', 'hsl(48 90% 38%)', 'hsl(0 0% 45%)'] },
  dark: { text: 'rgba(255,255,255,0.88)', muted: 'rgba(255,255,255,0.55)', track: 'rgba(255,255,255,0.1)', tick: 'rgba(255,255,255,0.85)',
    border: 'rgba(255,255,255,0.14)', bg: 'rgba(0,0,0,0.2)', ref: '#9fd8c2', refBg: 'rgba(76,175,80,0.18)',
    fills: ['hsl(209 70% 66%)', 'hsl(153 55% 58%)', 'hsl(31 100% 66%)', 'hsl(344 80% 70%)',
      'hsl(268 70% 76%)', 'hsl(186 60% 60%)', 'hsl(48 90% 62%)', 'hsl(0 0% 72%)'] },
};

const Figure = styled.figure`
  margin: 10px 0; padding: 10px 12px; border-radius: 8px; border: 1px solid ${(p) => p.c.border}; background: ${(p) => p.c.bg};
  color: ${(p) => p.c.text}; font-size: 12px; line-height: 1.35; font-variant-numeric: tabular-nums;
  figcaption { font-size: 11px; color: ${(p) => p.c.muted}; margin-top: 8px; }
  .title { font-weight: 600; font-size: 12.5px; margin: 0 0 6px; color: ${(p) => p.c.text}; }
  .legend { display: flex; flex-wrap: wrap; gap: 4px 12px; margin: 0 0 8px; font-size: 11px; color: ${(p) => p.c.muted}; }
  .legend i { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 5px; vertical-align: -1px; }
  .legend .tick-key { display: inline-block; width: 2px; height: 10px; margin-right: 5px; vertical-align: -1px; background: ${(p) => p.c.tick}; }
  .row { display: grid; grid-template-columns: minmax(0, 1fr); gap: 3px; margin: 0 0 9px; }
  .statement { color: ${(p) => p.c.text}; overflow-wrap: anywhere; }
  .ref { font-size: .85em; margin-right: 5px; padding: 0 4px; border-radius: 4px; text-decoration: none; color: ${(p) => p.c.ref}; background: ${(p) => p.c.refBg}; white-space: nowrap; }
  .bars { position: relative; display: grid; gap: 2px; }
  .bar { display: grid; grid-template-columns: minmax(0, 1fr) 40px; align-items: center; gap: 6px; }
  .track { height: 9px; border-radius: 3px; background: ${(p) => p.c.track}; overflow: hidden; }
  .fill { height: 100%; border-radius: 3px; }
  .val { font-size: 11px; color: ${(p) => p.c.muted}; text-align: right; }
  .tick { position: absolute; top: -2px; bottom: -2px; width: 2px; background: ${(p) => p.c.tick}; opacity: .9; pointer-events: none; }
`;
const Fallback = styled.pre` white-space: pre-wrap; font-size: 11px; `;

const pctText = (n, lang) => `${new Intl.NumberFormat(lang === 'en' ? 'en-GB' : lang === 'sv' ? 'sv-SE' : 'fi-FI').format(n)} %`;

export default function SplitBars({ body }) {
  const { t, language } = useTranslation();
  const { sources, tone } = useContext(SplitContext);
  const { block } = useMemo(() => parseSplitBlock(body), [body]);
  if (!block) return <Fallback><code>{String(body ?? '')}</code></Fallback>;
  const c = TONES[tone] || TONES.dark;
  // A row's ref is the base ref (V39) or a marker number; the source list numbers both
  const numOf = (ref) => (/^\d+$/.test(ref) ? Number(ref) : sources.find((s) => String(s.ref || '').toUpperCase() === ref)?.num || null);
  // The bars span the track width minus the value column, so the tick lands on the bars' own scale
  const tickLeft = (all) => `calc((100% - 46px) * ${all / 100})`;

  return (
    <Figure c={c} data-testid="split-bars">
      {block.title && <p className="title">{block.title}</p>}
      <div className="legend">
        {block.groups.map((g, i) => <span key={g.key}><i style={{ background: c.fills[i % c.fills.length] }} />{g.label}</span>)}
        {block.rows.some((r) => r.all != null) && <span><span className="tick-key" />{t('chatAgent.split.all')}</span>}
      </div>
      {block.rows.map((r, i) => {
        const num = numOf(r.ref);
        return (
          <div className="row" key={i}>
            <div className="statement">
              {num ? <a className="ref" href={`#src-${num}`}>[{num}]</a> : <span className="ref">{r.ref}</span>}
              {r.statement}
            </div>
            <div className="bars">
              {block.groups.map((g, gi) => (
                <div className="bar" key={g.key} title={`${g.label}: ${pctText(r.shares[g.key], language)}`}>
                  <div className="track"><div className="fill" style={{ width: `${r.shares[g.key]}%`, background: c.fills[gi % c.fills.length] }} /></div>
                  <span className="val">{pctText(r.shares[g.key], language)}</span>
                </div>
              ))}
              {r.all != null && <span className="tick" style={{ left: tickLeft(r.all) }} title={`${t('chatAgent.split.all')}: ${pctText(r.all, language)}`} />}
            </div>
          </div>
        );
      })}
      <figcaption>{t('chatAgent.split.note')}</figcaption>
    </Figure>
  );
}
